// Фактура стінок: візерунок — функція висоти h(u, v) ∈ [0, 1] від координат у періодах.
// Панель рельєфу — замкнене тіло: поверхня візерунку назовні від стінки, плоске дно трохи всередині неї.
// Усі візерунки парні по u (h(−u, v) = h(u, v)): кришку, повернуту на 180°, візерунок не розводить.
import type { Manifold, ManifoldToplevel } from 'manifold-3d/manifold';

export const PATTERNS = ['knurl', 'hex', 'ribs', 'wave', 'pyr', 'dots'] as const;
export type Pattern = (typeof PATTERNS)[number];

const tri = (x: number) => 1 - 2 * Math.abs(x - Math.round(x));
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const S3 = Math.sqrt(3) / 2;
/** Соти: ширина схилу між коміркою і канавкою, у періодах. */
const HEX_G = 0.18;
/** Крапки: радіус горбика, у періодах (сусіди — на відстані 1). */
const DOT_R = 0.38;

export function height(p: Pattern, u: number, v: number): number {
  switch (p) {
    case 'knurl': return Math.min(tri(u + v), tri(u - v));
    case 'pyr': return Math.min(tri(u), tri(v));
    case 'ribs': return clamp01((0.375 - Math.abs(u - Math.round(u))) / 0.25);
    case 'wave': return 0.5 + 0.5 * Math.cos(2 * Math.PI * (v + 0.25 * Math.cos(2 * Math.PI * u)));
    case 'dots': {
      // ряди в шаховому порядку
      const j0 = Math.round(v / S3);
      let d = Infinity;
      for (let j = j0 - 1; j <= j0 + 1; j++) {
        const off = j & 1 ? 0.5 : 0, i = Math.round(u - off);
        for (let k = i - 1; k <= i + 1; k++) d = Math.min(d, Math.hypot(u - k - off, v - j * S3));
      }
      return d < DOT_R ? 0.5 + 0.5 * Math.cos((Math.PI * d) / DOT_R) : 0;
    }
    case 'hex': {
      // центри комірок: (1, 0) і (½, √3/2); відстань до краю — по трьох напрямках сусідів
      const b0 = Math.round(v / S3), a0 = Math.round(u - 0.5 * b0);
      let d = Infinity;
      for (let b = b0 - 1; b <= b0 + 1; b++) for (let a = a0 - 1; a <= a0 + 1; a++) {
        const x = u - (a + 0.5 * b), y = v - b * S3;
        d = Math.min(d, Math.max(Math.abs(x), Math.abs(0.5 * x + S3 * y), Math.abs(0.5 * x - S3 * y)));
      }
      return clamp01((0.5 - d) / HEX_G);
    }
  }
}

/** Найкрутіший схил візерунку вздовж висоти (на період): глибина ≤ період / схил — нависання не крутіші за 45°. */
const SLOPE: Record<Pattern, number> = { knurl: 2, pyr: 2, ribs: 0, wave: Math.PI, dots: Math.PI / (2 * DOT_R), hex: 1 / HEX_G };
export const maxDepth = (p: Pattern, P: number) => (SLOPE[p] ? P / SLOPE[p] : Infinity);

/**
 * Крок сітки в періодах. Гранчасті візерунки точні вже на грубій сітці: їхні злами лежать на лініях сітки
 * або на діагоналях комірок (діагональ вибирається за візерунком). Плавні — дрібніше.
 */
const STEP: Record<Pattern, number> = { knurl: 1 / 4, pyr: 1 / 4, ribs: 1 / 8, wave: 1 / 12, dots: 1 / 12, hex: 1 / 16 };
/** Гранчасті: після об'єднання пласкі ділянки злипаються (simplify) — файл у рази менший. Плавним це не допомагає. */
export const FACETED = new Set<Pattern>(['knurl', 'pyr', 'ribs']);
const MIN_STEP = 0.25; // мм

/** Вузли сітки на [a, b]: кратні кроку (від нуля візерунку) плюс самі краї. */
function ticks(a: number, b: number, step: number) {
  const out = [a];
  for (let k = Math.floor(a / step) + 1; k * step < b - 1e-6; k++) if (k * step > a + 1e-6) out.push(k * step);
  out.push(b);
  return out;
}

export type Face = {
  /** Точка на поверхні стінки посередині грані (x, y) і нормаль назовні (горизонтальна, одинична). */
  cx: number; cy: number; nx: number; ny: number;
  /** Половина ширини панелі вздовж грані (від центру грані). */
  hw: number;
  /** Висота панелі на столі. */
  z0: number; z1: number;
  /** Координата візерунку по висоті: pv = sv·z + cv (мм). */
  sv: 1 | -1; cv: number;
  /** На скільки дно панелі заходить у стінку. */
  inset: number;
};

/**
 * Панель рельєфу на плоскій грані. Біля нижнього краю (як лежить на столі) рельєф зрізаний під 45° —
 * без нависання над порожнечею. D — глибина в мм, P — період у мм.
 */
export function relief(M: ManifoldToplevel, f: Face, pat: Pattern, P: number, D: number): Manifold {
  if (pat === 'hex') return hexRelief(M, f, P, D);
  const step = Math.max(STEP[pat] * P, MIN_STEP);
  const us = ticks(-f.hw, f.hw, step);
  // по висоті — вузли кратні кроку в координатах візерунку
  const pa = f.sv * f.z0 + f.cv, pb = f.sv * f.z1 + f.cv;
  const pvs = ticks(Math.min(pa, pb), Math.max(pa, pb), step);
  const zs = pvs.map((pv) => (pv - f.cv) / f.sv).sort((a, b) => a - b);
  const nu = us.length, nz = zs.length;
  // зріз під 45° знизу — обрізкою клином (clip), а не по сітці: на сітці він ішов би сходинками
  const w = (u: number, z: number) => D * height(pat, u / P, (f.sv * z + f.cv) / P);
  // U × Z = N: локальна трійка (u, z, w) права — обхід трикутників назовні
  const ux = -f.ny, uy = f.nx;
  const pos: number[] = [];
  const put = (u: number, z: number, d: number) => { pos.push(f.cx + u * ux + d * f.nx, f.cy + u * uy + d * f.ny, z); return pos.length / 3 - 1; };
  const top: number[] = [];
  const W: number[] = [];
  for (let j = 0; j < nz; j++) for (let i = 0; i < nu; i++) { const d = w(us[i], zs[j]); W.push(d); top.push(put(us[i], zs[j], d)); }
  const T = (i: number, j: number) => j * nu + i;
  const tri: number[] = [];
  for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nu - 1; i++) {
    const a = T(i, j), b = T(i + 1, j), c = T(i + 1, j + 1), d = T(i, j + 1);
    // діагональ, що проходить по зламу візерунку: та, чия середина ближча до висоти в центрі комірки
    const mid = w((us[i] + us[i + 1]) / 2, (zs[j] + zs[j + 1]) / 2);
    if (Math.abs((W[a] + W[c]) / 2 - mid) <= Math.abs((W[b] + W[d]) / 2 - mid)) tri.push(a, b, c, a, c, d);
    else tri.push(a, b, d, b, c, d);
  }
  // край за годинниковою стрілкою навпаки (проти, якщо дивитися ззовні): низ, право, верх, ліво
  const ring: number[] = [];
  for (let i = 0; i < nu - 1; i++) ring.push(T(i, 0));
  for (let j = 0; j < nz - 1; j++) ring.push(T(nu - 1, j));
  for (let i = nu - 1; i > 0; i--) ring.push(T(i, nz - 1));
  for (let j = nz - 1; j > 0; j--) ring.push(T(0, j));
  const bot = ring.map((t) => put(us[t % nu], zs[Math.floor(t / nu)], -f.inset - 0.1));
  const c = put(0, (f.z0 + f.z1) / 2, -f.inset - 0.1);
  for (let k = 0; k < ring.length; k++) {
    const k1 = (k + 1) % ring.length;
    const p = ring[k], q = ring[k1], bp = bot[k], bq = bot[k1];
    tri.push(top[p], bp, bq, top[p], bq, top[q], c, bq, bp);
  }
  const raw = new M.Manifold(new M.Mesh({ numProp: 3, vertProperties: new Float32Array(pos), triVerts: new Uint32Array(tri) }));
  const box = clipOf(M, f, D);
  const r = raw.intersect(box);
  raw.delete(); box.delete();
  return r;
}

/** Рамка панелі й зріз під 45° від нижнього краю (як лежить на столі): w ≤ z − z0. Опукле тіло. */
function clipOf(M: ManifoldToplevel, f: Face, D: number): Manifold {
  const ux = -f.ny, uy = f.nx;
  const pt = (u: number, z: number, w: number): [number, number, number] => [f.cx + u * ux + w * f.nx, f.cy + u * uy + w * f.ny, z];
  const wt = Math.min(D + 1, f.z1 - f.z0);
  const clip: [number, number, number][] = [];
  for (const u of [-f.hw, f.hw]) clip.push(pt(u, f.z0, -f.inset), pt(u, f.z1, -f.inset), pt(u, f.z1, wt), pt(u, f.z0 + wt, wt), pt(u, f.z0, 0));
  return M.Manifold.hull(clip);
}

/**
 * Соти точно: кожна комірка — шестигранна зрізана піраміда (низ — комірка цілком, верх — менший на схил),
 * панель — їх об'єднання, обрізане рамкою і зрізом під 45° знизу. На квадратній сітці похилі краї сот
 * виходили б сходинками.
 */
function hexRelief(M: ManifoldToplevel, f: Face, P: number, D: number): Manifold {
  const ux = -f.ny, uy = f.nx;
  // локальна точка (u — вздовж грані, pv — висота візерунку, w — назовні) → світ
  const at = (u: number, pv: number, w: number): [number, number, number] => [f.cx + u * ux + w * f.nx, f.cy + u * uy + w * f.ny, (pv - f.cv) / f.sv];
  const pa = Math.min(f.sv * f.z0 + f.cv, f.sv * f.z1 + f.cv), pb = Math.max(f.sv * f.z0 + f.cv, f.sv * f.z1 + f.cv);
  const R = P / (2 * S3); // від центру до вершини комірки (апофема — P/2)
  const ring = (cu: number, cv: number, k: number, w: number) => Array.from({ length: 6 }, (_, i) => {
    const a = Math.PI / 6 + (i * Math.PI) / 3;
    return at(cu + k * R * Math.cos(a), cv + k * R * Math.sin(a), w);
  });
  const cells: Manifold[] = [];
  const top = 1 - 2 * HEX_G; // верх комірки відносно низу
  for (let b = Math.floor(pa / (S3 * P)) - 1; b <= Math.ceil(pb / (S3 * P)) + 1; b++) {
    const cv = b * S3 * P;
    for (let a = Math.floor(-f.hw / P - 0.5 * b) - 1; a <= Math.ceil(f.hw / P - 0.5 * b) + 1; a++) {
      const cu = (a + 0.5 * b) * P;
      if (Math.abs(cu) > f.hw + P || cv < pa - P || cv > pb + P) continue;
      cells.push(M.Manifold.hull([...ring(cu, cv, 1, -f.inset - 0.1), ...ring(cu, cv, 1, 0), ...ring(cu, cv, top, D)]));
    }
  }
  const all = M.Manifold.union(cells), box = clipOf(M, f, D);
  const r = all.intersect(box);
  for (const c of cells) c.delete();
  all.delete(); box.delete();
  return r;
}

/** Мініатюра візерунку для плитки: два періоди, освітлення збоку. */
export function thumb(pat: Pattern, size = 64): string {
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const g = cv.getContext('2d')!;
  const img = g.createImageData(size, size);
  const k = 2 / size, e = 0.5 / size;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const u = (x - size / 2) * k, v = (size / 2 - y) * k;
    const h = height(pat, u, v);
    const dx = (height(pat, u + e, v) - height(pat, u - e, v)) / (2 * e), dy = (height(pat, u, v + e) - height(pat, u, v - e)) / (2 * e);
    // світло зліва згори; трохи висоти — щоб виступи були світліші
    const l = clamp01(0.55 + (-dx * 0.6 + dy * 0.6) * 0.06 + h * 0.25);
    const o = (y * size + x) * 4;
    img.data[o] = img.data[o + 1] = img.data[o + 2] = Math.round(40 + l * 200);
    img.data[o + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  return cv.toDataURL();
}
